import { useTranslations } from "@zapier/i18n";
import {
  HydratedAgent,
  useAgentActionPermissions,
  useSupportedAssets,
} from "@zapier/client";
import {
  KebabMenuItem,
  useEmitAgentKebabInteractionEvent,
} from "@zapier/shared";
import { AssetTypes, getBuildAssetUrl } from "@zapier/utils";
import { useCallback, useMemo } from "react";
import { AgentModal } from "./AgentModals";

interface Input {
  agent: HydratedAgent;
  setCurrentAgent: (agent: HydratedAgent | undefined) => void;
  setCurrentModal: (modal: AgentModal | undefined) => void;
  onCopyToAnotherAccount?: (asset: HydratedAgent) => void;
}

export function useAgentRow({
  agent,
  setCurrentModal,
  setCurrentAgent,
  onCopyToAnotherAccount,
}: Input) {
  const t = useTranslations("agents-table");
  const tAction = useTranslations("buttons");
  const { isMPFEnabled } = useSupportedAssets();
  const permissions = useAgentActionPermissions(agent.id ?? "");
  const { emit } = useEmitAgentKebabInteractionEvent();

  const agentUrl = getBuildAssetUrl(AssetTypes.Agent, agent.id);

  const handleDeleteAgent = useCallback(() => {
    setCurrentAgent(agent);
    setCurrentModal(AgentModal.Delete);
    emit("delete", agent.id);
  }, [agent, emit, setCurrentAgent, setCurrentModal]);

  const handleMoveAgent = useCallback(() => {
    setCurrentAgent(agent);
    setCurrentModal(AgentModal.Move);
    emit("move", agent.id);
  }, [agent, emit, setCurrentAgent, setCurrentModal]);

  const menuItems = useMemo<KebabMenuItem[]>(() => {
    const items = [
      isMPFEnabled && {
        disabled: !permissions.canMove,
        icon: "actionMoveToFolder",
        label: t("actions.move-to-folder"),
        onClick: handleMoveAgent,
      },
      onCopyToAnotherAccount
        ? {
            divider: true,
            label: "divider",
          }
        : false,
      onCopyToAnotherAccount
        ? {
            icon: "arrowExit",
            label: tAction("copy-to-another-account"),
            onClick: () => {
              onCopyToAnotherAccount(agent);
            },
          }
        : false,
      isMPFEnabled || onCopyToAnotherAccount
        ? {
            divider: true,
            label: "divider",
          }
        : false,
      {
        disabled: !permissions.canDelete,
        hasWarning: true,
        icon: "actionTrash",
        label: t("actions.delete"),
        onClick: handleDeleteAgent,
        tooltipText: permissions.isLoading
          ? undefined
          : t("permissions.no-delete-permission"),
      },
    ] satisfies (KebabMenuItem | false)[];

    return items.filter((item) => !!item);
  }, [
    permissions,
    handleDeleteAgent,
    handleMoveAgent,
    isMPFEnabled,
    onCopyToAnotherAccount,
    agent,
    t,
    tAction,
  ]);

  return {
    agentUrl,
    menuItems,
    triggerPermissionsCheck: permissions.enablePermissionsCheck,
  };
}
